// Phase C's sky on screen (seam 4, gate C3): the look per time and weather from the daylight table (`look.at(hours,
// weather)` and `look.indoor(profile)`, @rpg/look's DayLookSource), written each frame into the low-poly look, the sky
// dome (sun, moon, stars, clouds), the light shaft cards (every tier) and the screen-space shaft pass (high), with the
// rain as camera-wrapped particles (@rpg/vfx on three.quarks). A scene makes it before its low-poly look (the dome and
// the pass are the look's options), attaches it after, and calls `frame` once per drawn frame before the render.
// Nothing it changes after ready compiles a program: the warm-up draws every piece once, visible.
import * as THREE from "three";
import { applyDayLook, createDayLook, createShafts, createSkyDome, mixDayLook, ShaftPass, type DayLook, type DayLookSource, type DayTable, type LowPolyLook, type ShaftSpot, type Shafts } from "@rpg/look";
import { cameraWeather, createVfx, type CameraWeather, type EmitterSpec, type VfxSystem, type WeatherSettle } from "@rpg/vfx";
import type { WeatherBlend } from "@rpg/world";
import type { SpikeContent } from "../content";

/** How a capture settles the rain (IMP-0019; the harness's `settle()`, never play): 1.2 s of rain in 50 ms slices, as
 *  `world.shot` plays a shot's rain in, drawn from one seed, so a rain shot repeats to the byte and a dry one has no
 *  late drops. Agent's values (Q3, pending the user). */
export const CAPTURE_RAIN = { seed: 2002, seconds: 1.2, step: 0.05 } as const;

/** What the view reads each frame: the hour and the weather's blend (from the sim), and the room the camera is in. */
export interface SkyReading {
  hours: number;
  weather: WeatherBlend;
  /** An indoor profile (a room's light), or null outdoors. */
  indoor: string | null;
  /** How far the room's light has blended in, 0 to 1 (default 1: at once). */
  indoorMix?: number;
  /** Whole days on the sky's clock (default 0): the cloud shadow drifts on `day × 24 + hours` (@rpg/look 0088), so
   *  it carries on across midnight and a restored save restores it. */
  day?: number;
}

/** The sky's view. */
export interface SkyView {
  /** The look per time and weather (seam 4's `look.at` and `look.indoor`). */
  source: DayLookSource;
  /** Give these to `createLowPolyLook` (the dome; on high, the shaft pass and the depth it reads). */
  lookOptions: { sky: THREE.Mesh; post?: { depth: true; passes: [ShaftPass] } };
  /** Once the look exists: the shaft cards at `shafts` (on its haze) and the rain go into the scene. */
  attach(look: LowPolyLook, shafts: readonly ShaftSpot[]): void;
  /** Compute and apply the look for this frame; `dt` (s) steps the rain, `time` (s) drives the clouds and shafts. */
  frame(dt: number, time: number): void;
  /** Everything the warm-up must draw, made visible and active; the returned function puts it back. */
  forWarm(): () => void;
  /** The look last applied. */
  current(): DayLook | null;
  /** The outdoor look last computed (the same as `current` outdoors; a room's light is mixed over it). */
  outdoor(): DayLook | null;
  /** Capture only: the rain settles to what the weather's amount, the camera and the stream decide (none in clear
   *  weather; a full sheet from one seed in rain). The sim is untouched. Returns the amount and the drops left. */
  settle(o: WeatherSettle): { rain: number; drops: number };
  /** For `state()` and the specs. */
  report(): { hours: number; key: string; shafts: number; pass: number | null; rain: number; particles: number; indoor: string | null; stars: number; lamps: number; wet: number; exposure: number };
}

/** The daylight row as @rpg/look's table (the game's look overrides already merged over the unit's row at load), and
 *  its shafts' and rain's settings. */
export function readDaylight(content: SpikeContent, id = "valley"): { table: DayTable; shafts: { cards: number; pass: number; samples: number }; rain: { spec: EmitterSpec; above: number; ahead: number } } {
  const reg = content.registry;
  const row = reg.get("daylight", id);
  if (!row) throw new Error(`sky: no daylight row "${id}"`);
  const { reason: _r, shafts, rain, id: _id, ...table } = row;
  const e = reg.get("emitters", rain.emitter.id);
  if (!e) throw new Error(`sky: no emitter "${rain.emitter.id}"`);
  const { id: _eid, reason: _er, ...spec } = e;
  return { table: table as unknown as DayTable, shafts, rain: { spec: spec as unknown as EmitterSpec, above: rain.above, ahead: rain.ahead } };
}

/** Make the sky's view for a scene at a tier. `read` gives the hour, weather and room each frame. */
export function createSkyView(o: { content: SpikeContent; scene: THREE.Scene; camera: THREE.PerspectiveCamera; renderer: THREE.WebGLRenderer; tier: "low" | "high"; read: () => SkyReading }): SkyView {
  const d = readDaylight(o.content);
  const source = createDayLook(d.table);
  const dome = createSkyDome({ radius: o.camera.far * 0.9 });
  const pass = o.tier === "high" ? new ShaftPass({ samples: d.shafts.samples, gain: d.shafts.pass }) : null;
  let look: LowPolyLook | null = null;
  let shafts: Shafts | null = null;
  let vfx: VfxSystem | null = null;
  let rain: CameraWeather | null = null;
  let last: DayLook | null = null;
  let lastOutdoor: DayLook | null = null;
  let held: { amount: number } | null = null;
  const forward = new THREE.Vector3();

  const self: SkyView = {
    source,
    lookOptions: pass ? { sky: dome, post: { depth: true, passes: [pass] } } : { sky: dome },
    attach(l, spots) {
      look = l;
      shafts = createShafts({ spots, haze: l.uniforms, gain: d.shafts.cards });
      o.scene.add(shafts.mesh);
      vfx = createVfx();
      o.scene.add(vfx.root);
      rain = cameraWeather(vfx, { emitter: d.rain.spec, above: d.rain.above, ahead: d.rain.ahead });
      rain.emitter.object.name = "sky:rain";
    },
    frame(dt, time) {
      if (!look) return;
      const r = o.read();
      const outdoor = source.at(r.hours, r.weather, { day: r.day ?? 0 });
      lastOutdoor = outdoor;
      last = r.indoor ? mixDayLook(outdoor, source.indoor(r.indoor, outdoor), r.indoorMix ?? 1) : outdoor;
      applyDayLook({ look, renderer: o.renderer, camera: o.camera, dome, shafts, pass }, last, time);
      o.camera.getWorldDirection(forward);
      rain?.update(o.camera.position, held ? held.amount : last.rain, { x: forward.x, z: forward.z });
      vfx?.update(Math.max(0, dt));
    },
    forWarm() {
      // the shafts drawn and the rain falling, so their programs compile with the rest; the pass runs on high anyway
      const wasVisible = shafts?.mesh.visible ?? false;
      if (shafts) shafts.mesh.visible = true;
      held = { amount: 1 };
      rain?.update(o.camera.position, 1);
      vfx?.update(0.2);
      return () => {
        if (shafts) shafts.mesh.visible = wasVisible;
        held = null;
        // the warm-up's drops go: a clear boot has no rain falling
        rain?.update(o.camera.position, 0);
        rain?.emitter.system.restart();
      };
    },
    settle(s) {
      const amount = held ? held.amount : (last?.rain ?? 0);
      o.camera.getWorldDirection(forward);
      const drops = rain ? rain.settle(o.camera.position, amount, s, { x: forward.x, z: forward.z }) : 0;
      return { rain: amount, drops };
    },
    current: () => last,
    outdoor: () => lastOutdoor,
    report: () => ({
      hours: last?.hours ?? 0,
      key: last?.key.from ?? "sun",
      shafts: shafts?.strength() ?? 0,
      pass: pass ? pass.strength() : null,
      rain: rain?.amount() ?? 0,
      particles: vfx?.counts().particles ?? 0,
      indoor: last?.indoor ?? null,
      stars: last?.stars ?? 0,
      lamps: last?.lamps ?? 0,
      wet: last?.wet ?? 0,
      exposure: last?.exposure ?? 0,
    }),
  };
  return self;
}
